import { useState, useMemo } from "react";
import shallow from "zustand/shallow";
import { clsx } from "clsx";
import CustomModal from "../CustomModal/CustomModal";
import { useShelvingStore, useStoreRoom, useStoreSystem } from "../../../store";
import styles from "./EditTireRackModal.module.scss";
import { SizeBlock } from "../../SizeBlock/SizeBlock";
import { LocationOfObject, ResetForButton, SizeFromLeft, Trashcan } from "../../SVGasComponent";
import { Color, TireRackItemI } from "../../../store/types/store";
import { degToRad, radToDeg } from "three/src/math/MathUtils";
import { RotationOfObject } from "../../SVGasComponent/RotationOfObject";
import { RotateXAxis } from "../../SVGasComponent/RotateXAxis";
import { RotateYAxis } from "../../SVGasComponent/RotateYAxis";
import { RotateZAxis } from "../../SVGasComponent/RotateZAxis";
import { Stack, ToggleButtonGroup, Switch, styled, FormControlLabel, Button, ButtonGroup } from "@mui/material";
import { ToggleButton } from "../../shared/ui/ToggleButton";
import { isNewSite } from "../../../utils";

const StyledSwitch = styled(Switch)(() => ({
    width: 64,
    height: 28,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 2,
        transitionDuration: "300ms",
        overflow: "visible",
        "&.Mui-checked": {
            transform: "translateX(36px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                backgroundColor: "rgba(131, 130, 128, 0.2)",
                border: "1px solid rgba(255, 255, 255, 0.5)",
                boxShadow: "inset -4px -4px 9px rgba(255, 255, 255, 0.6), inset 4px 4px 14px #838280",
                opacity: 1,
            },
            "& .MuiSwitch-thumb": {
                backgroundColor: "#EB3D00",
                border: "1px solid #FBFDFD",
                boxShadow:
                    "2px 2px 4px rgba(235, 61, 0, 0.1), -1px -1px 12px rgba(235, 61, 0, 0.4), 1px 1px 12px rgba(235, 61, 0, 0.41)",
                width: 24,
                height: 24,
                position: "relative",
                "&::after": {
                    content: '""',
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "3px",
                    height: "8px",
                    backgroundColor: "#FBFDFD",
                    borderRadius: "1.5px",
                },
            },
        },
    },
    "& .MuiSwitch-thumb": {
        backgroundColor: "#838280",
        width: 24,
        height: 24,
        transition: "all 300ms",
        overflow: "visible",
    },
    "& .MuiSwitch-track": {
        borderRadius: 100,
        backgroundColor: "rgba(131, 130, 128, 0.2)",
        border: "1px solid rgba(255, 255, 255, 0.5)",
        boxShadow: "inset -4px -4px 9px rgba(255, 255, 255, 0.6), inset 4px 4px 14px #838280",
        opacity: 1,
        transition: "all 300ms",
    },
}));

const SwitchContainer = styled("div")({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "7px 10px",
    gap: "6px",
    width: 210,
    height: 42,
    background: "#ffffff",
    boxShadow: "inset 1px 1px 7px rgba(0, 0, 0, 0.06)",
    borderRadius: 45,
    position: "relative",
    overflow: "visible",
    "& .MuiSwitch-root": {
        overflow: "visible",
        "& .MuiSwitch-switchBase": {
            overflow: "visible",
        },
        "& .MuiSwitch-thumb": {
            overflow: "visible",
        },
    },
    "& .switch-label": {
        fontStyle: "normal",
        fontWeight: 400,
        fontSize: 13,
        lineHeight: "20px",
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        letterSpacing: "0.515625px",
        color: "#838280",
        margin: 0,
        flex: "none",
    },
});

const StyledButtonGroup = styled(ButtonGroup)({
    "& .MuiButtonGroup-root": {
        boxShadow: "none",
    },
    "& .MuiButton-root": {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: 13,
        lineHeight: "16px",
        letterSpacing: "0.515625px",
        textTransform: "none",
        padding: "8px 16px",
        minWidth: 80,
        maxWidth: 120,
        height: 35,
        borderRadius: 16,
        border: "none",
        color: "#EB3D00",
        backgroundColor: "rgba(235, 61, 0, 0.14)",
        boxSizing: "border-box",
        transition: "background-color 0.2s ease, color 0.2s ease",
        "&:hover": {
            backgroundColor: "rgba(235, 61, 0, 0.24)",
            transform: "none",
        },
        "&.Mui-selected, &.MuiButton-contained": {
            backgroundColor: "#EB3D00",
            color: "#FFFFFF",
            "&:hover": {
                backgroundColor: "#d63400",
                transform: "none",
            },
        },
        "&:not(:last-child)": {
            marginRight: "2px",
        },
    },
});

const ButtonGroupContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "flex-start",
    width: "fit-content",
    minWidth: "200px",
});

interface PropertyInterface {
    paddingLeft: number;
    paddingRight: number;
    paddingTop: number;
    paddingBottom: number;
    rotationX: number;
    rotationY: number;
    rotationZ: number;
}

interface ChangeFunctionInterface {
    name: keyof PropertyInterface;
    value: number;
}

const isNew = isNewSite();

const EditTireRackModal = () => {
    const { currentTireRackId, selectTireRack, constructions } = useStoreSystem(
        (state) => ({
            currentTireRackId: state.currentTireRackId,
            selectTireRack: state.selectTireRack,
            constructions: state.constructions,
        }),
        shallow
    );

    const { updateTireRack, tireRacks, removeTireRack } = useStoreRoom(
        (state) => ({
            updateTireRack: state.updateTireRack,
            tireRacks: state.tireRacks,
            removeTireRack: state.removeTireRack,
        }),
        shallow
    );

    const roomSizes = useStoreRoom(
        (state) => ({
            roomLength: state.length,
            roomHeight: state.height,
            roomDepth: state.depth,
        }),
        shallow
    );

    const currentTireRack = tireRacks.find((tireRack) => tireRack.id === currentTireRackId) || null;

    console.log("=== EditTireRackModal State ===");
    console.log("currentTireRack:", currentTireRack);
    console.log("availableOptions will be calculated...");

    // Получаем все элементы той же категории что и текущая стойка
    const categoryElements = useMemo(() => {
        if (!currentTireRack) return [];

        const allElements = Object.values(constructions).flatMap((construction) => construction.ELEMENTS);
        return allElements.filter((element) => element.PROPERTIES.CATEGORY === currentTireRack.category);
    }, [constructions, currentTireRack]);

    // Анализируем доступные опции
    const availableOptions = useMemo(() => {
        const options = {
            mountings: new Set<string>(),
            shelfTypes: new Set<string>(),
            types: new Set<string>(),
        };

        categoryElements.forEach((element) => {
            if (element.PROPERTIES.MOUNTING) options.mountings.add(element.PROPERTIES.MOUNTING);
            if (element.PROPERTIES.SHELF_TYPE) options.shelfTypes.add(element.PROPERTIES.SHELF_TYPE);
            if (element.PROPERTIES.TYPE) options.types.add(element.PROPERTIES.TYPE);
        });

        const result = {
            mountings: Array.from(options.mountings),
            shelfTypes: Array.from(options.shelfTypes),
            types: Array.from(options.types),
        };

        console.log("availableOptions:", result);
        return result;
    }, [categoryElements]);

    // Функция для поиска элемента по свойствам и замены стойки
    const replaceWithNewVariant = (mounting?: string, shelfType?: string, rackType?: string) => {
        console.log("=== replaceWithNewVariant ===");
        console.log("Parameters:", { mounting, shelfType, rackType });

        if (!currentTireRack) {
            console.log("No currentTireRack, returning");
            return;
        }

        console.log("categoryElements count:", categoryElements.length);

        const targetElement = categoryElements.find((element) => {
            const props = element.PROPERTIES;
            const match =
                (!mounting || props.MOUNTING === mounting) &&
                (!shelfType || props.SHELF_TYPE === shelfType) &&
                (!rackType || props.TYPE === rackType);

            if (mounting && props.MOUNTING !== mounting) return false;
            if (shelfType && props.SHELF_TYPE !== shelfType) return false;
            if (rackType && props.TYPE !== rackType) return false;

            return true;
        });

        console.log("targetElement found:", !!targetElement);
        if (targetElement) {
            console.log("targetElement details:", {
                name: targetElement.NAME,
                mounting: targetElement.PROPERTIES.MOUNTING,
                shelfType: targetElement.PROPERTIES.SHELF_TYPE,
                rackType: targetElement.PROPERTIES.TYPE,
            });

            let bitrixId = targetElement.BITRIX_OFFER_ID;
            let partsPrice = targetElement.PARTS?.reduce((acc, part) => acc + (part.price || 0), 0) || 0;
            let totalCost = targetElement.PRICE + partsPrice;

            if (isNew && targetElement?.newNomenkl) {
                const colorsItems = useShelvingStore.getState().colorsItems;
                const color: Color = colorsItems.tireRacks ?? colorsItems.brackets;
                // @ts-ignore
                const newNomenkl = targetElement.newNomenkl?.find((n) => n?.color === color);
                if (newNomenkl) {
                    bitrixId = +newNomenkl.bitrixId;
                    totalCost = +(newNomenkl.priceNomenkl || 0);
                }
            }

            const updatedTireRack: TireRackItemI = {
                ...currentTireRack,
                pricing: {
                    base_price: isNew ? totalCost : targetElement.PRICE,
                    total_cost: totalCost,
                },
                name: targetElement.NAME,
                mounting: mounting || currentTireRack.mounting,
                shelfType: shelfType || currentTireRack.shelfType,
                rackType: rackType || currentTireRack.rackType,
                category: currentTireRack.category,
                parts: isNew ? [] : targetElement.PARTS,
                nameNomenkl: targetElement.nameNomenkl,
                config: {
                    ...currentTireRack.config,
                    model_name: targetElement.CODE,
                    url: targetElement.PROPERTIES.FILE_3D,
                },
                bitrixId,
            };

            console.log("Updating tire rack with:", updatedTireRack);
            updateTireRack(updatedTireRack);
        } else {
            console.log("No matching element found");
            console.log(
                "Available elements:",
                categoryElements.map((el) => ({
                    name: el.NAME,
                    mounting: el.PROPERTIES.MOUNTING,
                    shelfType: el.PROPERTIES.SHELF_TYPE,
                    rackType: el.PROPERTIES.TYPE,
                }))
            );
        }
    };

    // Определяем размеры пола (для размещения на полу)
    const floorWidth = roomSizes.roomLength;
    const floorDepth = roomSizes.roomDepth;

    // Вычисляем текущие отступы от краев комнаты
    // Габариты объекта (предполагаем, что у TireRack есть размеры как у Decor)
    const objectWidth = currentTireRack?.width ?? 0;
    const objectDepth = currentTireRack?.depth ?? 0;

    const calculatePaddingLeft = (tireRack: TireRackItemI) => {
        // Позиция X в метрах, переводим в миллиметры и рассчитываем отступ от левого края
        const positionX = tireRack.position[0] * 1000;
        return floorWidth / 2 + positionX;
    };

    const calculatePaddingRight = (tireRack: TireRackItemI) => {
        // Позиция X в метрах, переводим в миллиметры и рассчитываем отступ от правого края
        const positionX = tireRack.position[0] * 1000;
        return floorWidth / 2 - positionX;
    };

    const calculatePaddingTop = (tireRack: TireRackItemI) => {
        // Расстояние от задней стенки
        const positionZ = tireRack.position[2] * 1000;
        const result = Math.round(floorDepth - positionZ);

        return result;
    };

    const calculatePaddingBottom = (tireRack: TireRackItemI) => {
        // Расстояние от передней стенки
        const positionZ = tireRack.position[2] * 1000;
        const result = Math.round(positionZ);

        console.log("calculatePaddingBottom:");
        console.log("positionZ (мм):", positionZ);
        console.log("result:", result);

        return result;
    };

    const [property, setProperty] = useState<PropertyInterface>({
        paddingLeft: Math.round(currentTireRack ? calculatePaddingLeft(currentTireRack) : 0),
        paddingRight: Math.round(currentTireRack ? calculatePaddingRight(currentTireRack) : 0),
        paddingTop: Math.round(currentTireRack ? calculatePaddingTop(currentTireRack) : 0),
        paddingBottom: Math.round(currentTireRack ? calculatePaddingBottom(currentTireRack) : 0),
        rotationX: Math.round(currentTireRack ? radToDeg(currentTireRack.rotation[0]) : 0),
        rotationY: Math.round(currentTireRack ? radToDeg(currentTireRack.rotation[1]) : 0),
        rotationZ: Math.round(currentTireRack ? radToDeg(currentTireRack.rotation[2]) : 0),
    });

    const setPropertyObject = ({ name, value }: ChangeFunctionInterface) => {
        const newProperty: PropertyInterface = { ...property };

        if (name === "paddingLeft") {
            newProperty.paddingLeft = Math.round(value);
            newProperty.paddingRight = Math.round(floorWidth - value);
        } else if (name === "paddingRight") {
            newProperty.paddingRight = Math.round(value);
            newProperty.paddingLeft = Math.round(floorWidth - value);
        } else if (name === "paddingTop") {
            newProperty.paddingTop = Math.round(value);
            newProperty.paddingBottom = Math.round(floorDepth - value);
        } else if (name === "paddingBottom") {
            newProperty.paddingBottom = Math.round(value);
            newProperty.paddingTop = Math.round(floorDepth - value);
        } else if (name.includes("rotation")) {
            newProperty[name] = Math.round(value);
        }

        setProperty(newProperty);
    };

    if (!currentTireRack) return null;

    const resetProperty = () => {
        setProperty({
            paddingLeft: calculatePaddingLeft(currentTireRack),
            paddingRight: calculatePaddingRight(currentTireRack),
            paddingTop: calculatePaddingTop(currentTireRack),
            paddingBottom: calculatePaddingBottom(currentTireRack),
            rotationX: radToDeg(currentTireRack.rotation[0]),
            rotationY: radToDeg(currentTireRack.rotation[1]),
            rotationZ: radToDeg(currentTireRack.rotation[2]),
        });
    };

    const acceptProperty = () => {
        // Вычисляем новую позицию
        const newPositionX = (property.paddingLeft - floorWidth / 2) / 1000;
        const newPositionZ = property.paddingBottom / 1000;

        // Отладка
        console.log("Отладка позиции Z:");
        console.log("paddingBottom:", property.paddingBottom);
        console.log("floorDepth:", floorDepth);
        console.log("newPositionZ:", newPositionZ);
        console.log("Текущая позиция Z:", currentTireRack.position[2]);

        const newPosition: [number, number, number] = [
            newPositionX,
            currentTireRack.position[1], // Y остается прежним
            newPositionZ,
        ];

        // Конвертируем углы в радианы
        const newRotation: [number, number, number] = [
            degToRad(property.rotationX),
            degToRad(property.rotationY),
            degToRad(property.rotationZ),
        ];

        updateTireRack({
            ...currentTireRack,
            position: newPosition,
            rotation: newRotation,
        });

        selectTireRack(null);
    };

    const deleteTireRack = () => {
        currentTireRackId && removeTireRack(currentTireRackId);
        selectTireRack(null);
    };

    return (
        <CustomModal
            isOpenModal={currentTireRackId !== null}
            onCloseModalCallback={() => {
                selectTireRack(null);
            }}
        >
            <>
                {/* <div className={styles.title}>{currentTireRack.name}</div> */}
                <div>
                    <div>
                        <div className={styles.blockName}>
                            <RotationOfObject width={24} height={24} />
                            <div className={styles.group}>Вращение объекта</div>
                        </div>
                        <div className={styles.blockSizes}>
                            <SizeBlock
                                icon={
                                    <div className={styles.propsIcon}>
                                        <RotateXAxis />
                                    </div>
                                }
                                value={property.rotationX}
                                step={10}
                                min={0}
                                max={360}
                                name="rotationX"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={styles.propsIcon}>
                                        <RotateYAxis />
                                    </div>
                                }
                                value={property.rotationY}
                                step={10}
                                min={0}
                                max={360}
                                name="rotationY"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={styles.propsIcon}>
                                        <RotateZAxis />
                                    </div>
                                }
                                value={property.rotationZ}
                                step={10}
                                min={0}
                                max={360}
                                name="rotationZ"
                                changeFunction={setPropertyObject}
                            />
                        </div>
                    </div>
                    <div>
                        <div className={styles.blockName}>
                            <LocationOfObject />
                            <div className={styles.group}>Расположение объекта</div>
                        </div>
                        <div className={styles.blockSizes}>
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon, styles.propsIcon_plus180)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingLeft}
                                step={100}
                                min={objectWidth / 2}
                                max={floorWidth - objectWidth / 2}
                                name="paddingLeft"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon, styles.propsIcon_plus90)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingTop}
                                step={100}
                                min={objectDepth / 2}
                                max={floorDepth - objectDepth / 2}
                                name="paddingTop"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingRight}
                                step={100}
                                min={objectWidth / 2}
                                max={floorWidth - objectWidth / 2}
                                name="paddingRight"
                                changeFunction={setPropertyObject}
                            />
                            <SizeBlock
                                icon={
                                    <div className={clsx(styles.propsIcon, styles.propsIcon_minus90)}>
                                        <SizeFromLeft />
                                    </div>
                                }
                                value={property.paddingBottom}
                                step={100}
                                min={objectDepth / 2}
                                max={floorDepth - objectDepth / 2}
                                name="paddingBottom"
                                changeFunction={setPropertyObject}
                            />
                        </div>
                    </div>

                    {/* Блок переключателей свойств */}

                    <div>
                        <div className={styles.blockName}>
                            <RotationOfObject width={24} height={24} />
                            <div className={styles.group}>Варианты модели</div>
                        </div>
                        <div className={styles.propertyToggles}>
                            {availableOptions.mountings.length > 1 && (
                                <div className={styles.propertyGroup}>
                                    <span className={styles.propertyLabel}>ДОБАВИТЬ НОЖКИ</span>
                                    <SwitchContainer>
                                        <span className="switch-label">{availableOptions.mountings[0]}</span>
                                        <StyledSwitch
                                            checked={currentTireRack?.mounting === availableOptions.mountings[1]}
                                            onChange={(event) => {
                                                console.log("=== Switch onChange ===");
                                                console.log("event.target.checked:", event.target.checked);
                                                console.log("currentTireRack?.mounting:", currentTireRack?.mounting);
                                                console.log("availableOptions.mountings:", availableOptions.mountings);

                                                const newMounting = event.target.checked
                                                    ? availableOptions.mountings[1]
                                                    : availableOptions.mountings[0];

                                                console.log("newMounting:", newMounting);
                                                console.log("currentTireRack?.shelfType:", currentTireRack?.shelfType);
                                                console.log("currentTireRack?.rackType:", currentTireRack?.rackType);

                                                replaceWithNewVariant(
                                                    newMounting,
                                                    currentTireRack?.shelfType,
                                                    currentTireRack?.rackType
                                                );
                                            }}
                                            inputProps={{ "aria-label": "mounting switch" }}
                                        />
                                        <span className="switch-label">{availableOptions.mountings[1]}</span>
                                    </SwitchContainer>
                                </div>
                            )}

                            {availableOptions.shelfTypes.length > 1 && (
                                <div className={styles.propertyGroup}>
                                    <span className={styles.propertyLabel}>ТИП ПОЛКИ</span>
                                    <ButtonGroupContainer>
                                        <StyledButtonGroup>
                                            {availableOptions.shelfTypes.map((shelfType) => (
                                                <Button
                                                    key={shelfType}
                                                    variant={
                                                        currentTireRack?.shelfType === shelfType
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    onClick={() =>
                                                        replaceWithNewVariant(
                                                            currentTireRack?.mounting,
                                                            shelfType,
                                                            currentTireRack?.rackType
                                                        )
                                                    }
                                                >
                                                    {shelfType}
                                                </Button>
                                            ))}
                                        </StyledButtonGroup>
                                    </ButtonGroupContainer>
                                </div>
                            )}

                            {availableOptions.types.length > 1 && (
                                <div className={styles.propertyGroup}>
                                    <span className={styles.propertyLabel}>ШИРИНА</span>
                                    <ButtonGroupContainer>
                                        <StyledButtonGroup>
                                            {availableOptions.types.map((type) => (
                                                <Button
                                                    key={type}
                                                    variant={
                                                        currentTireRack?.rackType === type ? "contained" : "outlined"
                                                    }
                                                    onClick={() =>
                                                        replaceWithNewVariant(
                                                            currentTireRack?.mounting,
                                                            currentTireRack?.shelfType,
                                                            type
                                                        )
                                                    }
                                                >
                                                    {type}
                                                </Button>
                                            ))}
                                        </StyledButtonGroup>
                                    </ButtonGroupContainer>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.buttonBlock_main}>
                    <div className={styles.buttonBlock_left}>
                        <button
                            className={clsx(styles.buttonBlock_button, styles.buttonBlock_button__secondary)}
                            onClick={resetProperty}
                        >
                            Сброс
                            <ResetForButton />
                        </button>
                        <button
                            className={clsx(styles.buttonBlock_button, styles.buttonBlock_button__primary)}
                            onClick={deleteTireRack}
                        >
                            <Trashcan />
                        </button>
                    </div>
                    <div className={styles.buttonBlock_right}>
                        <button
                            className={clsx(styles.buttonBlock_button, styles.buttonBlock_button__accept)}
                            onClick={acceptProperty}
                        >
                            Принять
                        </button>
                    </div>
                </div>
            </>
        </CustomModal>
    );
};

export default EditTireRackModal;
